import { Dispatch, Fragment, SetStateAction, useEffect, useState } from "react"

// ** Third Party Components
import axios from "axios"
import printJS from "print-js"
import { parseCookies } from "nookies"
import { Rating } from "react-simple-star-rating"
import {
  Check,
  FileText,
  FileSpreadsheet,
  FileImage,
  Film,
  Presentation,
  File,
  Printer,
  X,
  Download,
  Eye,
} from "lucide-react"

// ** UI Components
import { Button } from "../common/button"
import { Avatar, AvatarImage } from "../common/avatar"
import { Dialog, DialogContent } from "../common/dialog"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** API Routes
import { SERVER_URL, WORK_DIARY_GET_NOTE_BY_ID } from "@pma/utils/api-routes"

// ** Types
import { DictType } from "@pma/types"
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Helpers
import { cn, parseMessage } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

interface WorkDiaryDetailsProps {
  workDiaryDetailsModal: boolean
  selectedWorkDiary: any
  project: DictType | undefined
  setWorkDiaryDetailsModal: Dispatch<SetStateAction<boolean>>
  setSelectedWorkDiary: Dispatch<SetStateAction<DictType | null>>
}

const FILE_TYPES: Record<string, string[]> = {
  image: ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"],
  video: ["mp4", "webm", "avi", "mov", "mkv", "wmv"],
  pdf: ["pdf"],
  excel: ["xlsx", "xls", "csv"],
  word: ["doc", "docx"],
  ppt: ["ppt", "pptx"],
}

const TYPE_STYLES: Record<string, { icon: typeof FileText; color: string; badge: string }> = {
  pdf: { icon: FileText, color: "text-red-500", badge: "bg-red-100 text-red-700" },
  excel: { icon: FileSpreadsheet, color: "text-green-600", badge: "bg-green-100 text-green-700" },
  word: { icon: FileText, color: "text-blue-600", badge: "bg-blue-100 text-blue-700" },
  ppt: { icon: Presentation, color: "text-orange-500", badge: "bg-orange-100 text-orange-700" },
  video: { icon: Film, color: "text-purple-500", badge: "bg-purple-100 text-purple-700" },
  image: { icon: FileImage, color: "text-cyan-500", badge: "bg-cyan-100 text-cyan-700" },
  other: { icon: File, color: "text-gray-500", badge: "bg-gray-100 text-gray-700" },
}

const getFileType = (fileName: string): string => {
  const ext = fileName?.split(".").pop()?.toLowerCase() || ""
  return Object.entries(FILE_TYPES).find(([, exts]) => exts.includes(ext))?.[0] || "other"
}

const FileTypeIcon = ({ type, className }: { type: string; className?: string }) => {
  const { icon: Icon, color } = TYPE_STYLES[type] || TYPE_STYLES.other
  return <Icon className={cn("h-8 w-8", color, className)} />
}

const AttachmentCard = ({
  file,
  index,
  isHebrew,
}: {
  file: any
  index: number
  isHebrew: boolean
}) => {
  const { translate } = useTranslator()
  const fileType = getFileType(file?.name)
  const isImage = fileType === "image"
  const isVideo = fileType === "video"
  const [imageError, setImageError] = useState(false)
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)

  const ActionButtons = ({ className = "" }: { className?: string }) => (
    <div className={cn("flex items-center justify-center gap-2", className)}>
      {isImage && (
        <button
          onClick={() => setIsPreviewOpen(true)}
          className='rounded-full bg-white p-2 text-gray-700 hover:bg-gray-100'
          title='Preview'
        >
          <Eye className='h-4 w-4' />
        </button>
      )}
      <a
        href={file.file}
        download={file?.name}
        target='_blank'
        rel='noopener noreferrer'
        className='rounded-full bg-white p-2 text-gray-700 hover:bg-gray-100'
        title='Download'
      >
        <Download className='h-4 w-4' />
      </a>
    </div>
  )

  return (
    <Fragment>
      <div className='group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-all hover:shadow-md'>
        <div className='relative h-32 w-full overflow-hidden bg-gray-50'>
          {isImage && !imageError ? (
            <>
              <img
                src={file.file}
                alt={file?.name}
                className='h-full w-full object-cover transition-transform group-hover:scale-105'
                onError={() => setImageError(true)}
              />
              <div className='absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100'>
                <ActionButtons />
              </div>
            </>
          ) : isVideo ? (
            <div className='relative h-full w-full'>
              <video
                src={file.file}
                className='h-full w-full object-cover'
                muted
                preload='metadata'
              />
              <div className='absolute inset-0 flex items-center justify-center bg-black/20'>
                <div className='rounded-full bg-white/90 p-3'>
                  <Film className='h-6 w-6 text-purple-500' />
                </div>
              </div>
              <div className='absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 transition-opacity group-hover:opacity-100'>
                <ActionButtons />
              </div>
            </div>
          ) : (
            <div className='flex h-full w-full flex-col items-center justify-center gap-2'>
              <FileTypeIcon type={fileType} />
              <span
                className={cn(
                  "rounded-full px-2 py-0.5 text-xs font-medium uppercase",
                  TYPE_STYLES[fileType]?.badge || TYPE_STYLES.other.badge,
                )}
              >
                {fileType === "other" ? file?.name?.split(".").pop() : fileType}
              </span>
            </div>
          )}
        </div>
        <div className='p-3'>
          <p
            className={cn(
              "truncate text-sm font-medium text-gray-700",
              isHebrew ? "text-right" : "text-left",
            )}
            title={file?.name}
          >
            {file?.name}
          </p>
          <div className={cn("mt-2 flex gap-2", isHebrew ? "flex-row-reverse" : "flex-row")}>
            <a
              href={file.file}
              download={file?.name}
              target='_blank'
              rel='noopener noreferrer'
              className='flex flex-1 items-center justify-center gap-1 rounded bg-blue-50 px-2 py-1.5 text-xs font-medium text-blue-600 hover:bg-blue-100'
            >
              <Download className='h-3 w-3' />
              {translate("Download")}
            </a>
          </div>
        </div>
      </div>
      {isImage && isPreviewOpen && (
        <Dialog open={isPreviewOpen} onOpenChange={setIsPreviewOpen}>
          <DialogContent className='max-h-[90vh] max-w-4xl overflow-auto p-2'>
            <div className='relative'>
              <img
                src={file.file}
                alt={file?.name}
                className='max-h-[80vh] w-full object-contain'
              />
              <div className='mt-2 flex items-center justify-between px-2'>
                <p className='text-sm text-gray-600'>{file?.name}</p>
                <a
                  href={file.file}
                  download={file?.name}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex items-center gap-1 rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700'
                >
                  <Download className='h-4 w-4' />
                  {translate("Download")}
                </a>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </Fragment>
  )
}

// Reusable table component with fixed column widths
const DataTable = ({
  headers,
  data,
  renderRow,
}: {
  headers: string[]
  data: any[]
  renderRow: (item: any, idx: number) => React.ReactNode
}) => {
  const { locale } = useTranslator()
  const isHebrew = locale === "he"

  return (
    <table className='min-w-full divide-y divide-gray-200'>
      <thead className='bg-gray-100'>
        <tr>
          {headers.map((h, i) => (
            <th
              key={i}
              className={cn(
                "px-4 py-3 text-xs font-medium uppercase tracking-wider text-gray-500",
                i === 0 ? "w-1/2" : "w-1/4",
                isHebrew ? "text-right" : "text-left",
              )}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className='divide-y divide-gray-200 bg-white'>{data?.map(renderRow)}</tbody>
    </table>
  )
}

const WorkDiaryDetails = ({
  workDiaryDetailsModal,
  selectedWorkDiary,
  setWorkDiaryDetailsModal,
  setSelectedWorkDiary,
  project,
}: WorkDiaryDetailsProps) => {
  const { authConfig } = useAuth()
  const { translate, locale } = useTranslator()

  const isHebrew = locale === "he"
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const organizationUsers = authConfig?.organizationUsers || []

  const [comments, setComments] = useState<any[]>([])
  const [isLoadingComments, setIsLoadingComments] = useState(false)

  const handleResetState = () => {
    setWorkDiaryDetailsModal(!workDiaryDetailsModal)
    setSelectedWorkDiary(null)
  }

  const fetchComments = async () => {
    if (!selectedWorkDiary?.id) return
    setIsLoadingComments(true)
    try {
      const { data } = await axios.get(
        `${SERVER_URL}${WORK_DIARY_GET_NOTE_BY_ID}/${selectedWorkDiary.id}`,
        { headers: { Authorization: `Bearer ${accessToken}` } },
      )
      setComments(data || [])
    } catch {
      setComments([])
    } finally {
      setIsLoadingComments(false)
    }
  }

  useEffect(() => {
    if (selectedWorkDiary?.id && workDiaryDetailsModal) fetchComments()
  }, [selectedWorkDiary?.id, workDiaryDetailsModal])

  const handlePrint = () => printJS({ printable: "print-section", type: "html", scanStyles: false })

  const formatDate = (date: string) => (isHebrew ? date?.split("-").reverse().join("-") : date)

  const ApprovalBadge = ({ approved }: { approved: boolean }) => (
    <span
      className={cn(
        "flex items-center",
        approved ? "text-green-600" : "text-red-600",
        isHebrew ? "mr-1" : "ml-1",
      )}
    >
      {translate(approved ? "Approved" : "Not Approved")}
      {approved ? <Check size={16} /> : <X size={16} />}
    </span>
  )

  const SectionTitle = ({ children }: { children: React.ReactNode }) => (
    <h3 className='mb-3 border-b pb-2 text-lg font-bold'>{children}</h3>
  )

  const filterResources = (type: string) =>
    selectedWorkDiary?._diary_resourses?.filter((obj: any) => obj._resource[type]) || []
  const getResourceName = (item: any) => (isHebrew ? item?._resource?.he : item?._resource?.en)

  return (
    <Fragment>
      <Dialog open={workDiaryDetailsModal} onOpenChange={handleResetState}>
        <DialogContent className={cn("z-50 h-[85vh] w-full max-w-4xl", isHebrew ? "rtl" : "ltr")}>
          <div className='h-[80vh] overflow-auto px-4 py-4 pt-2 sm:px-6 sm:py-6'>
            {/* Header */}
            <div className='mb-4 flex items-center justify-center pb-4'>
              <div>
                <h2 className='text-center text-3xl font-semibold'>{translate("Work Logs")}</h2>
                <p className='text-center text-lg text-gray-500'>
                  <strong>{translate("Log Number")}</strong>: {selectedWorkDiary?.number}
                </p>
                <p className='text-center text-sm text-gray-500'>
                  {translate("Date")}: {formatDate(selectedWorkDiary?.diary_date)}
                </p>
                <div className='mt-2 text-center'>
                  <Button variant='outline' size='sm' onClick={handlePrint}>
                    <Printer size={16} className={cn(isHebrew ? "ml-2" : "mr-2")} />
                    {translate("Print")}
                  </Button>
                </div>
              </div>
            </div>

            {/* Project & Status */}
            <div className='mb-8 grid grid-cols-1 gap-6 md:grid-cols-2'>
              <div>
                <SectionTitle>{translate("Project Details")}</SectionTitle>
                <div className='space-y-2'>
                  <div>
                    <strong>{translate("Project")}:</strong> {project?.title}
                  </div>
                  <div>
                    <strong>{translate("Contractor Company Name")}:</strong>{" "}
                    {selectedWorkDiary?._company?.name}
                  </div>
                  <div>
                    <strong>{translate("Site Address")}:</strong>{" "}
                    {selectedWorkDiary?.address || "-"}
                  </div>
                </div>
              </div>
              <div>
                <SectionTitle>{`${translate("Status")} & ${translate("Progress")}`}</SectionTitle>
                <div className='space-y-2'>
                  <div>
                    <strong>{translate("Status")}:</strong> {isHebrew ? selectedWorkDiary?._status?.he : selectedWorkDiary?._status?.en}
                  </div>
                  <div className='flex items-center'>
                    <strong>{translate("Contractor Approval")}:</strong>
                    <ApprovalBadge approved={selectedWorkDiary?.contractor_approval} />
                  </div>
                  <div className='flex items-center'>
                    <strong>{translate("Supervisor Approval")}:</strong>
                    <ApprovalBadge approved={selectedWorkDiary?.supervisor_approval} />
                  </div>
                </div>
              </div>
            </div>

            <div className='mb-8'>
              <SectionTitle>{translate("Description and Content")}</SectionTitle>
              <div className='space-y-2'>
                <div>
                  {selectedWorkDiary?.description}
                </div>
              </div>
            </div>

            {/* Weather */}
            <div className='mb-8'>
              <SectionTitle>{translate("Weather Conditions")}</SectionTitle>
              <div className='rounded-lg bg-blue-50 p-4'>
                <p className='flex items-center'>
                  {isHebrew ? selectedWorkDiary?._weather?.he : selectedWorkDiary?._weather?.en} (
                  {selectedWorkDiary?.temperature}°C)
                  <Rating
                    size={18}
                    initialValue={selectedWorkDiary?.weather_rating}
                    readonly
                    SVGstyle={{ display: "inline-block" }}
                  />
                </p>
              </div>
            </div>

            {/* Work Details */}
            <div className='mb-8'>
              <SectionTitle>{translate("Work Details")}</SectionTitle>
              {selectedWorkDiary?._work_details?.length > 0 ? (
                <DataTable
                  headers={[translate("Description"), translate("Quantity"), translate("Unit")]}
                  data={selectedWorkDiary?._work_details}
                  renderRow={(item, idx) => (
                    <tr key={idx} className='hover:bg-gray-50'>
                      <td className='w-1/2 px-4 py-4 text-sm text-gray-900'>{item?.description}</td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.amount}</td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.units}</td>
                    </tr>
                  )}
                />
              ) : (
                <p className='py-4 text-center text-sm text-gray-500'>
                  {translate("No work details found")}
                </p>
              )}
            </div>

            {/* Resources */}
            <div className='mb-8'>
              <SectionTitle>{translate("Resources")}</SectionTitle>
              {filterResources("structures")?.length > 0 ? (
                <DataTable
                  headers={[translate("Description"), translate("Model"), translate("Unit")]}
                  data={filterResources("structures")}
                  renderRow={(item, idx) => (
                    <tr key={idx} className='hover:bg-gray-50'>
                      <td className='w-1/2 px-4 py-4 text-sm text-gray-900'>
                        {getResourceName(item)}
                      </td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.amount}</td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.remarks}</td>
                    </tr>
                  )}
                />
              ) : (
                <p className='py-4 text-center text-sm text-gray-500'>
                  {translate("No resources found")}
                </p>
              )}
            </div>

            {/* Workforce */}
            <div className='mb-8'>
              <SectionTitle>{translate("Workforce")}</SectionTitle>
              {filterResources("employees")?.length > 0 ? (
                <DataTable
                  headers={[translate("Description"), translate("Quantity"), translate("Remark")]}
                  data={filterResources("employees")}
                  renderRow={(item, idx) => (
                    <tr key={idx} className='hover:bg-gray-50'>
                      <td className='w-1/2 px-4 py-4 text-sm text-gray-900'>
                        {getResourceName(item)}
                      </td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.amount}</td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.remarks}</td>
                    </tr>
                  )}
                />
              ) : (
                <p className='py-4 text-center text-sm text-gray-500'>
                  {translate("No workforce found")}
                </p>
              )}
            </div>

            {/* Tools */}
            <div className='mb-8'>
              <SectionTitle>{translate("Tools")}</SectionTitle>
              {filterResources("tools")?.length > 0 ? (
                <DataTable
                  headers={[translate("Description"), translate("Quantity"), translate("Remarks")]}
                  data={filterResources("tools")}
                  renderRow={(item, idx) => (
                    <tr key={idx} className='hover:bg-gray-50'>
                      <td className='w-1/2 px-4 py-4 text-sm text-gray-900'>
                        {getResourceName(item)}
                      </td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.amount}</td>
                      <td className='w-1/4 px-4 py-4 text-sm text-gray-900'>{item?.remarks}</td>
                    </tr>
                  )}
                />
              ) : (
                <p className='py-4 text-center text-sm text-gray-500'>
                  {translate("No tools found")}
                </p>
              )}
            </div>

            {/* Tasks */}
            <div className='mb-8'>
              <SectionTitle>{translate("Tasks")}</SectionTitle>
              {selectedWorkDiary?._tasks?.length > 0 ? (
                <ul className='space-y-2'>
                  {selectedWorkDiary?._tasks?.map((task: any, index: number) => (
                    <li key={index} className='flex items-center gap-2 text-gray-500'>
                      <span className={cn("flex", task?.finish_to == 1 && "line-through")}>
                        <strong>{task?.description} -</strong>
                        <p className={cn(isHebrew ? "mr-2" : "ml-2")}>
                          {organizationUsers
                            .filter((u: any) => task.responsible_id?.includes(u.id))
                            .map((user: any) => (
                              <Avatar key={user.id} className='h-5 w-5'>
                                <AvatarImage
                                  src={
                                    user?.user_picture?.url ||
                                    `${process.env.PUBLIC_URL}/user-profile.svg`
                                  }
                                />
                              </Avatar>
                            ))}
                        </p>
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='py-4 text-center text-sm text-gray-500'>
                  {translate("No tasks found for this diary")}
                </p>
              )}
            </div>

            {/* Comments */}
            <div className='mb-8'>
              <SectionTitle>{translate("Comments")}</SectionTitle>
              <div className={cn("max-h-60 space-y-4 overflow-y-auto", isHebrew ? "pl-2" : "pr-2")}>
                {isLoadingComments ? (
                  <p className='py-4 text-center text-sm text-gray-500'>
                    {translate("Loading")}...
                  </p>
                ) : comments.length ? (<>
                  {comments.map((c: any) => {
                    const isContractor = c._is_contractor
                    const roleBadge = c._is_contractor
                      ? { label: translate("Contractor"), color: "bg-orange-100 text-orange-700" }
                      : c._is_supervisor
                        ? { label: translate("Supervisor"), color: "bg-green-100 text-green-700" }
                        : c._is_financial
                          ? { label: translate("Financial"), color: "bg-purple-100 text-purple-700" }
                          : null

                    return (
                      <div
                        key={c.id}
                        className={cn(
                          "flex items-start gap-3",
                          isContractor ? "flex-row" : "flex-row-reverse",
                        )}
                      >
                        <div className='flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-100 font-medium text-blue-600'>
                          {c.user?.name?.charAt(0)?.toUpperCase() || "U"}
                        </div>
                        <div
                          className={cn(
                            "max-w-[75%] rounded-lg p-3",
                            isContractor ? "bg-gray-50" : "bg-blue-50",
                          )}
                        >
                          <div className={cn("mb-1 flex items-center gap-2 flex-nowrap whitespace-nowrap")}>
                            <span className='text-sm font-medium text-gray-900'>{c.user?.name}</span>
                            {roleBadge && (
                              <span className={cn("rounded-full px-2 py-0.5 text-[10px] font-medium", roleBadge.color)}>
                                {roleBadge.label}
                              </span>
                            )}
                            <span className='text-xs text-gray-500'>
                              {new Date(c.created_at).toLocaleString()}
                            </span>
                          </div>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: parseMessage(c?.note, organizationUsers),
                            }}
                            className={cn(
                              "allow-list p-2 ps-3 text-xs text-slate-600",
                              isContractor
                                ? isHebrew ? "text-right" : "text-left"
                                : isHebrew ? "text-left" : "text-right",
                            )}
                          />
                        </div>
                      </div>
                    )
                  })}</>
                ) : (
                  <p className='py-4 text-center text-sm text-gray-500'>
                    {translate("No comments yet")}.
                  </p>
                )}
              </div>
            </div>

            {/* Attachments */}
            <div className='mb-8'>
              <SectionTitle>{translate("Attachments")}</SectionTitle>
              {selectedWorkDiary?.files_id?.length > 0 ? (
                <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
                  {selectedWorkDiary.files_id.map((file: any, i: number) => (
                    <AttachmentCard key={i} file={file} index={i} isHebrew={isHebrew} />
                  ))}
                </div>
              ) : (
                <p className='py-4 text-center text-sm text-gray-500'>
                  {translate("No attachments")}
                </p>
              )}
            </div>

            {/* Footer */}
            <div className='mt-12 flex justify-between border-t pt-8'>
              <div className='text-sm text-gray-500'>
                {translate("Work Logs")} #{selectedWorkDiary?.number} | {translate("Project")}:{" "}
                {project?.title}
              </div>
              <div className='text-sm text-gray-500'>{selectedWorkDiary?.diary_date}</div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Print Section */}
      <div id='print-section' className='hidden' dir={isHebrew ? "rtl" : "ltr"}>
        <div style={{ padding: 24, fontFamily: "Arial, sans-serif", color: "#000" }}>
          <h2>{translate("Work Logs")}</h2>
          <p>
            <strong>{translate("Log Number")}:</strong> {selectedWorkDiary?.number}
          </p>
          <p>
            <strong>{translate("Date")}:</strong> {formatDate(selectedWorkDiary?.diary_date)}
          </p>

          <h3>{translate("Project Details")}</h3>
          <p>
            <strong>{translate("Project")}:</strong> {project?.title}
          </p>
          <p>
            <strong>{translate("Contractor Company Name")}:</strong>{" "}
            {selectedWorkDiary?._company?.name}
          </p>
          <p>
            <strong>{translate("Site Address")}:</strong> {selectedWorkDiary?.address || "-"}
          </p>

          <h3>
            {translate("Status")} & {translate("Progress")}
          </h3>
          <p>
            <strong>{translate("Status")}:</strong> {isHebrew ? selectedWorkDiary?._status?.he : selectedWorkDiary?._status?.en}
          </p>
          <p>
            <strong>{translate("Contractor Approval")}:</strong>{" "}
            {translate(selectedWorkDiary?.contractor_approval ? "Approved" : "Not Approved")}
          </p>
          <p>
            <strong>{translate("Supervisor Approval")}:</strong>{" "}
            {translate(selectedWorkDiary?.supervisor_approval ? "Approved" : "Not Approved")}
          </p>

          <h3>{translate("Work Details")}</h3>
          <table style={{ borderCollapse: "collapse", width: "100%" }} border={1}>
            <thead>
              <tr>
                <th style={{ width: "50%" }}>{translate("Description")}</th>
                <th style={{ width: "25%" }}>{translate("Quantity")}</th>
                <th style={{ width: "25%" }}>{translate("Unit")}</th>
              </tr>
            </thead>
            <tbody>
              {selectedWorkDiary?._work_details?.map((item: any, idx: number) => (
                <tr key={idx}>
                  <td>{item?.description}</td>
                  <td>{item?.amount}</td>
                  <td>{item?.units}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {[
            { title: "Resources", type: "structures" },
            { title: "Workforce", type: "employees" },
            { title: "Tools", type: "tools" },
          ].map(({ title, type }) => (
            <Fragment key={type}>
              <h3>{translate(title)}</h3>
              <ul>
                {filterResources(type).map((d: any, i: number) => (
                  <li key={i}>
                    <strong>{getResourceName(d)}:</strong> {d?.amount} ({d?.remarks})
                  </li>
                ))}
              </ul>
            </Fragment>
          ))}

          <h3>{translate("Tasks")}</h3>
          <ul>
            {selectedWorkDiary?._tasks?.map((t: any, i: number) => (
              <li key={i}>{t?.description}</li>
            ))}
          </ul>

          <h3>{translate("Comments")}</h3>
          <ul>
            {comments?.map((c: any, i: number) => (
              <li key={i} style={{ marginBottom: 8 }}>
                <strong>{c.user?.name}:</strong>
                <div
                  dangerouslySetInnerHTML={{ __html: parseMessage(c?.note, organizationUsers) }}
                />
                <small style={{ color: "#666" }}>({new Date(c.created_at).toLocaleString()})</small>
              </li>
            ))}
          </ul>

          <h3>{translate("Attachments")}</h3>
          <div>
            {selectedWorkDiary?.files_id?.map((f: any, i: number) => {
              const fileType = getFileType(f?.name)
              return (
                <div key={i} style={{ marginBottom: 12 }}>
                  {fileType === "image" ? (
                    <div>
                      <img
                        src={f.file}
                        alt={f?.name}
                        style={{ maxWidth: "100%", maxHeight: 300, objectFit: "contain" }}
                      />
                      <p style={{ fontSize: 12, color: "#666", marginTop: 4 }}>{f?.name}</p>
                    </div>
                  ) : (
                    <p>{f?.name}</p>
                  )}
                </div>
              )
            })}
          </div>

          <div style={{ marginTop: 20, fontSize: 12, color: "#888" }}>
            {translate("Work Logs")} #{selectedWorkDiary?.number} | {translate("Project")}:{" "}
            {project?.title}
            <br />
            {selectedWorkDiary?.diary_date}
          </div>
        </div>
      </div>
    </Fragment>
  )
}

export default WorkDiaryDetails
